import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import Api from "api/Api";

const api = new Api();

const initialState = {
  companies: [],
  selectedCompany: null,
  // Legacy fields for backward compatibility
  id: null,
  name: "",
  secret: "",
  apps: [],
  status: "idle",
  error: null,
};

export const fetchCompany = createAsyncThunk(
  "company/fetchCompany",
  async () => {
    // Fetch user info which includes all companies with unreads count
    const meResponse = await api.getUserInfo();

    // Fetch detailed info for all companies in parallel
    const companyDetailsPromises = meResponse.companies.map(c =>
      api.getCompanyInfoById(c.id)
    );
    const companyDetails = await Promise.all(companyDetailsPromises);

    // Create a map of company details by ID for easy lookup
    const detailsMap = {};
    companyDetails.forEach(details => {
      detailsMap[details.companyId] = details;
    });

    // Map companies from /me endpoint, enriching with detailed data
    const companies = meResponse.companies.map(c => ({
      companyId: c.id,
      companyName: c.name,
      unreads: c.unreads,
      role: c.role,
      // Add detailed info from the fetched details
      companySecret: detailsMap[c.id]?.companySecret || null,
      companySlug: detailsMap[c.id]?.companySlug || null,
      apps: detailsMap[c.id]?.apps || []
    }));

    return companies;
  }
);

export const fetchCompanySubscription = createAsyncThunk(
  "company/fetchCompanySubscription",
  async (companyId) => {
    const response = await api
      .getCompanySubscription(companyId)
      .then((companySubscription) => {
        return companySubscription;
      });

    return response;
  }
);

export const fetchCompanyPricing = createAsyncThunk(
  "pricing/fetchCompanyPricing",
  async (companyId) => {
    const response = await api.getPricingInfo(companyId).then((pricingInfo) => {
      return pricingInfo;
    });

    return response;
  }
);

export const switchCompany = createAsyncThunk(
  "company/switchCompany",
  async (companyId) => {
    // Fetch detailed info for the selected company
    const companyInfo = await api.getCompanyInfoById(companyId);
    return companyInfo;
  }
);


const companySlice = createSlice({
  name: "company",
  initialState,
  reducers: {
    resetCompany: () => {
      return initialState;
    },
    selectCompany: (state, action) => {
      const company = action.payload;
      state.selectedCompany = company;
      // Update legacy fields for backward compatibility
      state.id = company.companyId;
      state.name = company.companyName;
      state.secret = company.companySecret;
      state.apps = company.apps || [];
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchCompany.pending, (state, action) => {
        state.status = "loading";
      })
      .addCase(fetchCompany.fulfilled, (state, action) => {
        state.status = "succeeded";

        const companies = action.payload;
        state.companies = companies;

        // Select the first company
        const selected = companies[0];
        state.selectedCompany = selected || null;

        // No active company membership, e.g. the user has been deactivated
        if (!selected) {
          return;
        }

        // Legacy fields for backward compatibility
        state.id = selected.companyId;
        state.name = selected.companyName;
        state.secret = selected.companySecret;
        state.apps = selected.apps || [];
      })
      .addCase(fetchCompany.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(fetchCompanySubscription.pending, (state, action) => {
        state.status = "loading";
      })
      .addCase(fetchCompanySubscription.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.subscription = action.payload;
      })
      .addCase(fetchCompanySubscription.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(fetchCompanyPricing.pending, (state, action) => {
        state.status = "loading";
      })
      .addCase(fetchCompanyPricing.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.pricing = action.payload;
      })
      .addCase(fetchCompanyPricing.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(switchCompany.pending, (state, action) => {
        state.status = "loading";
      })
      .addCase(switchCompany.fulfilled, (state, action) => {
        state.status = "succeeded";
        const companyInfo = action.payload;

        // Update the company in the companies array
        const companyIndex = state.companies.findIndex(c => c.companyId === companyInfo.companyId);
        if (companyIndex !== -1) {
          state.companies[companyIndex] = {
            ...state.companies[companyIndex],
            companySecret: companyInfo.companySecret,
            companySlug: companyInfo.companySlug,
            apps: companyInfo.apps
          };
        }

        // Select this company
        state.selectedCompany = state.companies[companyIndex];

        // Update legacy fields
        state.id = companyInfo.companyId;
        state.name = companyInfo.companyName;
        state.secret = companyInfo.companySecret;
        state.apps = companyInfo.apps || [];
      })
      .addCase(switchCompany.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      });
  },
});

export const { resetCompany, selectCompany } = companySlice.actions;

export default companySlice.reducer;

export const selectCompanyInfo = (state) => state.company;
export const selectSelectedCompany = (state) => state.company.selectedCompany;
export const selectCompanies = (state) => state.company.companies;
